import { createYooptaEditor, YooptaContentValue } from '@yoopta/editor';
import Paragraph from '@yoopta/paragraph';
import Blockquote from '@yoopta/blockquote';
import Headings from '@yoopta/headings';
import Lists from '@yoopta/lists';
import Code from '@yoopta/code';
import Image from '@yoopta/image';
import Video from '@yoopta/video';
import File from '@yoopta/file';
import Callout from '@yoopta/callout';
import Accordion from '@yoopta/accordion';
import Divider from '@yoopta/divider';
import Table from '@yoopta/table';
import { markdown } from '@yoopta/exports';
import { mediaUploadService } from '../services/mediaUploadService';

/**
 * Get current user ID for uploads
 */
const getCurrentUserId = (): string => {
  // This will be passed from the component context
  return 'current-user'; // Placeholder - will be replaced by actual user ID
};

/**
 * Configure Image plugin with upload handler
 */
const configureImagePlugin = (userId?: string, bucketName: string = "proposalimages") => {
  return Image.extend({
    options: {
      async onUpload(file: File) {
        try {
          console.log('🖼️ Yoopta Image Upload triggered:', file.name);
          const result = await mediaUploadService.uploadImage(
            file,
            userId || getCurrentUserId(),
            (progress) => {
              console.log(`📊 Upload progress: ${progress.percentage}%`);
            },
            bucketName
          );

          if (!result.success) {
            throw new Error(result.error || 'Upload failed');
          }

          console.log('✅ Image uploaded successfully:', result.url);
          return {
            src: result.url!,
            alt: file.name,
            sizes: {
              width: 800,
              height: 600
            }
          };
        } catch (error) {
          console.error('❌ Image upload failed:', error);
          throw error;
        }
      }
    }
  });
};

/**
 * Configure Video plugin with upload handler
 */
const configureVideoPlugin = (userId?: string, bucketName: string = "proposalimages") => {
  return Video.extend({
    options: {
      async onUpload(file: File) {
        try {
          console.log('🎥 Yoopta Video Upload triggered:', file.name);
          const result = await mediaUploadService.uploadVideo(
            file,
            userId || getCurrentUserId(),
            (progress) => {
              console.log(`📊 Upload progress: ${progress.percentage}%`);
            },
            bucketName
          );

          if (!result.success) {
            throw new Error(result.error || 'Upload failed');
          }

          console.log('✅ Video uploaded successfully:', result.url);
          return {
            src: result.url!,
            poster: '', // Could add thumbnail generation later
            sizes: {
              width: 800,
              height: 450
            }
          };
        } catch (error) {
          console.error('❌ Video upload failed:', error);
          throw error;
        }
      }
    }
  });
};

/**
 * Configure File plugin with upload handler
 */
const configureFilePlugin = (userId?: string, bucketName: string = "proposalimages") => {
  return File.extend({
    options: {
      async onUpload(file: File) {
        try {
          console.log('📄 Yoopta File Upload triggered:', file.name);
          const result = await mediaUploadService.uploadFile(
            file,
            userId || getCurrentUserId(),
            (progress) => {
              console.log(`📊 Upload progress: ${progress.percentage}%`);
            },
            bucketName
          );

          if (!result.success) {
            throw new Error(result.error || 'Upload failed');
          }

          console.log('✅ File uploaded successfully:', result.url);
          return {
            src: result.url!,
            name: file.name,
            size: file.size,
            format: file.type
          };
        } catch (error) {
          console.error('❌ File upload failed:', error);
          throw error;
        }
      }
    }
  });
};

/**
 * Get configured plugins with upload handlers
 */
export const getConfiguredPlugins = (userId?: string, bucketName: string = "proposalimages") => [
  Paragraph,
  Blockquote,
  Headings.HeadingOne,
  Headings.HeadingTwo,
  Headings.HeadingThree,
  Lists.BulletedList,
  Lists.NumberedList,
  Code,
  Table, // 🎉 TABLE SUPPORT ADDED!
  Divider,
  configureImagePlugin(userId, bucketName),
  configureVideoPlugin(userId, bucketName),
  configureFilePlugin(userId, bucketName),
  Callout,
  Accordion,
];

/**
 * Create a safe Yoopta value with fallback content
 */
export const createSafeYooptaValue = (content: string = 'Start editing...'): YooptaContentValue => {
  const id = generateId();
  return {
    [id]: {
      id,
      type: 'Paragraph',
      value: [
        {
          id: generateId(),
          type: 'paragraph',
          children: [{ text: content }],
          props: {}
        }
      ],
      meta: {
        order: 0,
        depth: 0
      }
    }
  };
};

// Core plugin configuration for stable conversion (avoiding type conflicts)
const plugins = [
  Paragraph,
  Blockquote,
  Headings.HeadingOne,
  Headings.HeadingTwo,
  Headings.HeadingThree,
  Lists.BulletedList,
  Lists.NumberedList,
  Code,
  Divider,
];

// Extended plugins for advanced features (can be added later)
const extendedPlugins = [
  Image,
  Video,
  File,
  Callout,
  Accordion,
  Table,
];

// Create a shared editor instance for conversions
const conversionEditor = createYooptaEditor();

console.log('🔧 Conversion editor created with plugins available:', plugins.length);

/**
 * Generate a unique ID for Yoopta elements
 */
function generateId(): string {
  return Date.now().toString() + Math.random().toString(36).substr(2, 9);
}

/**
 * Create a default Yoopta paragraph with content
 */
function createDefaultYooptaParagraph(content: string): YooptaContentValue {
  const id = generateId();
  return {
    [id]: {
      id,
      type: 'Paragraph',
      value: [
        {
          id: generateId(),
          type: 'paragraph',
          children: [{ text: content }],
          props: {}
        }
      ],
      meta: {
        order: 0,
        depth: 0
      }
    }
  };
}

/**
 * Parse markdown text formatting (bold, italic) into Yoopta text nodes
 * Handles nested formatting and prevents double-formatting
 */
function parseTextWithFormatting(text: string): any[] {
  if (!text || typeof text !== 'string') {
    return [{ text: '' }];
  }

  // Clean up any existing markdown formatting first
  let cleanText = text;
  const result: any[] = [];
  let currentIndex = 0;
  
  // Find bold text **text** - more precise regex
  const boldRegex = /\*\*([^*]+?)\*\*/g;
  const boldMatches = [];
  let match;
  
  // Get all bold matches
  while ((match = boldRegex.exec(text)) !== null) {
    boldMatches.push({
      index: match.index,
      length: match[0].length,
      text: match[1],
      type: 'bold'
    });
  }
  
  // Sort by position
  boldMatches.sort((a, b) => a.index - b.index);
  
  // If no formatting, return simple text
  if (boldMatches.length === 0) {
    return [{ text }];
  }
  
  // Process text with formatting
  for (const boldMatch of boldMatches) {
    // Add text before this match
    if (boldMatch.index > currentIndex) {
      const beforeText = text.slice(currentIndex, boldMatch.index);
      if (beforeText.trim()) {
        result.push({ text: beforeText });
      }
    }
    
    // Add bold text
    result.push({ text: boldMatch.text, bold: true });
    
    currentIndex = boldMatch.index + boldMatch.length;
  }
  
  // Add remaining text
  if (currentIndex < text.length) {
    const remainingText = text.slice(currentIndex);
    if (remainingText.trim()) {
      result.push({ text: remainingText });
    }
  }
  
  return result.length > 0 ? result : [{ text }];
}

/**
 * Parse markdown manually and create Yoopta structure with enhanced support
 */
function parseMarkdownManually(markdownContent: string): YooptaContentValue {
  // Clean the content first to prevent double formatting
  let cleanedContent = markdownContent;
  if (cleanedContent) {
    // Remove nested bold formatting
    cleanedContent = cleanedContent.replace(/\*\*\*\*([^*]+?)\*\*\*\*/g, '**$1**'); // ****text**** -> **text**
    cleanedContent = cleanedContent.replace(/\*\*([^*]+?)\s\*\*/g, '**$1**'); // **text ** -> **text**
    cleanedContent = cleanedContent.replace(/\*\*\s([^*]+?)\*\*/g, '**$1**'); // ** text** -> **text**
  }
  
  const lines = cleanedContent.split('\n');
  const result: YooptaContentValue = {};
  let order = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const blockId = `block-${Date.now()}-${order}`;
    const elementId = `${blockId}-element`;

    if (line.trim() === '') {
      continue; // Skip empty lines
    }

    // Table detection (GFM format: | Header 1 | Header 2 |)
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      // Check if next line is a separator (|---|---|)
      const isTableStart = i + 1 < lines.length &&
                           lines[i + 1].trim().startsWith('|') &&
                           lines[i + 1].includes('---');

      if (isTableStart) {
        // Parse the entire table
        const tableRows: any[] = [];
        let j = i;

        // Collect all table rows
        while (j < lines.length && lines[j].trim().startsWith('|') && lines[j].trim().endsWith('|')) {
          const currentLine = lines[j].trim();

          // Skip separator row
          if (!currentLine.includes('---')) {
            // Parse row cells
            const cells = currentLine
              .substring(1, currentLine.length - 1) // Remove leading/trailing |
              .split('|')
              .map(cell => cell.trim());

            const rowId = `${blockId}-row-${tableRows.length}`;
            const rowChildren = cells.map((cellText, cellIdx) => ({
              id: `${rowId}-cell-${cellIdx}`,
              type: 'table-data-cell',
              children: [{ text: cellText }],
              props: {}
            }));

            tableRows.push({
              id: rowId,
              type: 'table-row',
              children: rowChildren,
              props: {}
            });
          }

          j++;
        }

        // Create Table block
        if (tableRows.length > 0) {
          result[blockId] = {
            id: blockId,
            type: 'Table',
            value: [{
              id: elementId,
              type: 'table',
              children: tableRows,
              props: {
                headerRow: true,
                columns: tableRows[0]?.children.length || 0
              }
            }],
            meta: { order, depth: 0 }
          };

          i = j - 1; // Skip to after table
          order++;
          continue;
        }
      }
    }

    // Code block detection (```language)
    if (line.startsWith('```')) {
      const language = line.substring(3).trim() || 'javascript';
      let codeContent = '';
      let j = i + 1;

      // Collect code content until closing ```
      while (j < lines.length && !lines[j].startsWith('```')) {
        codeContent += lines[j] + '\n';
        j++;
      }

      result[blockId] = {
        id: blockId,
        type: 'Code',
        value: [{
          id: elementId,
          type: 'code',
          children: [{ text: codeContent.trim() }],
          props: { language }
        }],
        meta: { order, depth: 0 }
      };

      i = j; // Skip to after closing ```
    }
    // Divider detection (--- or ***)
    else if (line.trim() === '---' || line.trim() === '***') {
      result[blockId] = {
        id: blockId,
        type: 'Divider',
        value: [{
          id: elementId,
          type: 'divider',
          children: [{ text: '' }],
          props: {}
        }],
        meta: { order, depth: 0 }
      };
    }
    // Heading detection
    else if (line.startsWith('# ')) {
      result[blockId] = {
        id: blockId,
        type: 'HeadingOne',
        value: [{
          id: elementId,
          type: 'heading-one',
          children: [{ text: line.substring(2) }],
          props: {}
        }],
        meta: { order, depth: 0 }
      };
    } else if (line.startsWith('## ')) {
      result[blockId] = {
        id: blockId,
        type: 'HeadingTwo',
        value: [{
          id: elementId,
          type: 'heading-two',
          children: [{ text: line.substring(3) }],
          props: {}
        }],
        meta: { order, depth: 0 }
      };
    } else if (line.startsWith('### ')) {
      result[blockId] = {
        id: blockId,
        type: 'HeadingThree',
        value: [{
          id: elementId,
          type: 'heading-three',
          children: [{ text: line.substring(4) }],
          props: {}
        }],
        meta: { order, depth: 0 }
      };
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      result[blockId] = {
        id: blockId,
        type: 'BulletedList',
        value: [{
          id: elementId,
          type: 'bulleted-list',
          children: [{ text: line.substring(2) }],
          props: {}
        }],
        meta: { order, depth: 0 }
      };
    } else if (/^\d+\.\s/.test(line)) {
      result[blockId] = {
        id: blockId,
        type: 'NumberedList',
        value: [{
          id: elementId,
          type: 'numbered-list',
          children: [{ text: line.replace(/^\d+\.\s/, '') }],
          props: {}
        }],
        meta: { order, depth: 0 }
      };
    } else if (line.startsWith('> ')) {
      result[blockId] = {
        id: blockId,
        type: 'Blockquote',
        value: [{
          id: elementId,
          type: 'blockquote',
          children: [{ text: line.substring(2) }],
          props: {}
        }],
        meta: { order, depth: 0 }
      };
    } 
    // HTML img tag detection (for images with dimensions): <img src="..." alt="..." width="..." height="...">
    else if (line.match(/^<img\s[^>]*src\s*=\s*[^>]*>/i)) {
      const imgMatch = line.match(/<img\s[^>]*src\s*=\s*["']([^"']+)["'][^>]*>/i);
      const altMatch = line.match(/alt\s*=\s*["']([^"']*)["']/i);
      const widthMatch = line.match(/width\s*=\s*["']?(\d+)["']?/i);
      const heightMatch = line.match(/height\s*=\s*["']?(\d+)["']?/i);
      
      if (imgMatch) {
        const src = imgMatch[1];
        const alt = altMatch ? altMatch[1] : 'Image';
        const width = widthMatch ? parseInt(widthMatch[1]) : null;
        const height = heightMatch ? parseInt(heightMatch[1]) : null;
        
        result[blockId] = {
          id: blockId,
          type: 'Image',
          value: [{
            id: elementId,
            type: 'image',
            children: [{ text: '' }],
            props: {
              src: src,
              alt: alt,
              name: alt || 'Image',
              width: width,
              height: height,
              size: null,
              format: null
            }
          }],
          meta: { order, depth: 0 }
        };
      } else {
        // Fallback to paragraph if regex fails
        result[blockId] = {
          id: blockId,
          type: 'Paragraph',
          value: [{
            id: elementId,
            type: 'paragraph',
            children: parseTextWithFormatting(line),
            props: {}
          }],
          meta: { order, depth: 0 }
        };
      }
    }
    // Image detection: ![alt](url) or ![alt](url "width=X height=Y")
    else if (line.match(/^!\[.*?\]\(.*?\)$/)) {
      const imageMatch = line.match(/^!\[(.*?)\]\((.*?)(?:\s+"([^"]*)")?\)$/);
      if (imageMatch) {
        const [, alt, src, dimensions] = imageMatch;

        // Parse dimensions from title attribute
        let width = null;
        let height = null;
        if (dimensions) {
          const widthMatch = dimensions.match(/width=(\d+)/);
          const heightMatch = dimensions.match(/height=(\d+)/);
          if (widthMatch) width = parseInt(widthMatch[1]);
          if (heightMatch) height = parseInt(heightMatch[1]);
        }

        result[blockId] = {
          id: blockId,
          type: 'Image',
          value: [{
            id: elementId,
            type: 'image',
            children: [{ text: '' }],
            props: {
              src: src,
              alt: alt,
              name: alt || 'Image',
              width: width,
              height: height,
              size: null,
              format: null
            }
          }],
          meta: { order, depth: 0 }
        };
      } else {
        // Fallback to paragraph if regex fails
        result[blockId] = {
          id: blockId,
          type: 'Paragraph',
          value: [{
            id: elementId,
            type: 'paragraph',
            children: parseTextWithFormatting(line),
            props: {}
          }],
          meta: { order, depth: 0 }
        };
      }
    } 
    // File/Link detection: [text](url)
    else if (line.match(/^\[.*?\]\(.*?\)$/) && !line.startsWith('![')) {
      const linkMatch = line.match(/^\[(.*?)\]\((.*?)\)$/);
      if (linkMatch) {
        const [, text, url] = linkMatch;
        // If it looks like a file or video, create appropriate block
        if (text.startsWith('Video:')) {
          result[blockId] = {
            id: blockId,
            type: 'Video',
            value: [{
              id: elementId,
              type: 'video',
              children: [{ text: '' }],
              props: {
                src: url,
                name: text.replace('Video: ', ''),
                width: 800,
                height: 450
              }
            }],
            meta: { order, depth: 0 }
          };
        } else if (text.startsWith('File:')) {
          result[blockId] = {
            id: blockId,
            type: 'File',
            value: [{
              id: elementId,
              type: 'file',
              children: [{ text: '' }],
              props: {
                src: url,
                name: text.replace('File: ', ''),
                size: null,
                format: null
              }
            }],
            meta: { order, depth: 0 }
          };
        } else {
          // Regular paragraph with link
          result[blockId] = {
            id: blockId,
            type: 'Paragraph',
            value: [{
              id: elementId,
              type: 'paragraph',
              children: parseTextWithFormatting(line),
              props: {}
            }],
            meta: { order, depth: 0 }
          };
        }
    } else {
        // Fallback to paragraph
      result[blockId] = {
        id: blockId,
        type: 'Paragraph',
        value: [{
          id: elementId,
          type: 'paragraph',
          children: parseTextWithFormatting(line),
            props: {}
          }],
          meta: { order, depth: 0 }
        };
      }
    } else {
      // Default to paragraph with formatting support
      result[blockId] = {
        id: blockId,
        type: 'Paragraph',
        value: [{
          id: elementId,
          type: 'paragraph',
          children: parseTextWithFormatting(line),
          props: {}
        }],
        meta: { order, depth: 0 }
      };
    }

    order++;
  }

  return result;
}

/**
 * Convert markdown content to Yoopta format
 */
export function convertMarkdownToYoopta(markdownContent: string): YooptaContentValue {
  console.log('🔄 Converting markdown to Yoopta:', {
    length: markdownContent?.length || 0,
    preview: markdownContent ? markdownContent.substring(0, 100) + '...' : 'no content'
  });

  // Always create a fallback first
  const fallbackValue = createDefaultYooptaParagraph(markdownContent || 'Empty content');

  if (!markdownContent || !markdownContent.trim()) {
    console.log('📝 Empty markdown, using default paragraph');
    return fallbackValue;
  }

  try {
    console.log('🔄 Attempting manual markdown parsing...');

    // Use manual parser for reliable conversion
    const yooptaValue = parseMarkdownManually(markdownContent);

    // Check if the result is valid and not empty
    if (yooptaValue && typeof yooptaValue === 'object' && Object.keys(yooptaValue).length > 0) {
      console.log('✅ Markdown converted successfully with manual parser:', {
        keys: Object.keys(yooptaValue),
        keyCount: Object.keys(yooptaValue).length
      });
      return yooptaValue;
    } else {
      console.log('⚠️ Manual parser returned empty/invalid value, using fallback');
      return fallbackValue;
    }
  } catch (error) {
    console.error('❌ Manual parsing failed:', error);
    console.log('🔄 Using fallback paragraph with content');
    return fallbackValue;
  }
}

/**
 * Convert Yoopta content to markdown - SIMPLE APPROACH
 */
/**
 * Convert formatted text children array back to markdown
 * Prevents double-formatting and cleans up existing markdown
 */
function convertTextChildrenToMarkdown(children: any[]): string {
  if (!children || !Array.isArray(children)) {
    return '';
  }

  return children.map(child => {
    let text = child.text || '';
    
    // Clean up any existing markdown formatting in the text first
    text = text.replace(/\*\*(.*?)\*\*/g, '$1'); // Remove existing bold
    text = text.replace(/\*(.*?)\*/g, '$1'); // Remove existing italic
    text = text.replace(/`(.*?)`/g, '$1'); // Remove existing code
    
    // Apply formatting only if the flag is set
    if (child.bold && text.trim()) {
      text = `**${text}**`;
    }
    if (child.italic && text.trim()) {
      text = `*${text}*`;
    }
    if (child.code && text.trim()) {
      text = `\`${text}\``;
    }
    if (child.underline && text.trim()) {
      text = `<u>${text}</u>`;
    }
    
    return text;
  }).join('');
}

export function convertYooptaToMarkdown(yooptaValue: YooptaContentValue): string {
  try {
    if (!yooptaValue || typeof yooptaValue !== 'object') {
      return '';
    }

    // Get all blocks and sort by order
    const blocks = Object.values(yooptaValue)
      .filter((block: any) => block && typeof block === 'object')
      .sort((a: any, b: any) => (a.meta?.order || 0) - (b.meta?.order || 0));

    let result = '';

    for (const block of blocks as any[]) {
      if (!block) continue;
      
      let text = '';
      
      // Process block types
      
      // Handle different block types
      if (block.type === 'Image' && block.value) {
        // Handle image blocks - value is an array, get first element
        let imageData = block.value;
        if (Array.isArray(imageData) && imageData.length > 0) {
          imageData = imageData[0]; // Get the actual image data from array
        }
        
        // Extract image data from props or children
        let src = '';
        let alt = 'Image';
        let width = null;
        let height = null;
        
        if (imageData && imageData.props) {
          console.log('🖼️ FULL IMAGE DATA PROPS:', JSON.stringify(imageData.props, null, 2));
          console.log('🖼️ FULL IMAGE DATA STRUCTURE:', JSON.stringify(imageData, null, 2));
          src = imageData.props.src || imageData.props.url || '';
          alt = imageData.props.alt || imageData.props.name || 'Image';
          width = imageData.props.width || imageData.props.w || null;
          height = imageData.props.height || imageData.props.h || null;
        }
        
        // Check children for image data
        if (!src && imageData && imageData.children && Array.isArray(imageData.children)) {
          for (const child of imageData.children) {
            if (child && (child.src || child.url)) {
              src = child.src || child.url;
              alt = child.alt || child.name || alt;
              width = child.width || child.w || width;
              height = child.height || child.h || height;
              break;
            }
          }
        }
        
        // Check if we have custom dimensions (resized by user)
        if (src) {
          if ((width && width !== 'auto') || (height && height !== 'auto')) {
            // Use special markdown syntax with dimensions in title attribute
            const dimensions = [];
            if (width && width !== 'auto') dimensions.push(`width=${width}`);
            if (height && height !== 'auto') dimensions.push(`height=${height}`);
            const dimensionString = dimensions.length > 0 ? ` "${dimensions.join(' ')}"` : '';
            text = `![${alt}](${src}${dimensionString})`;
          } else {
            // Use standard markdown image syntax
            text = `![${alt}](${src})`;
          }
        }
      } else if (block.type === 'Video' && block.value) {
        // Handle video blocks - value might be an array
        let videoData = block.value;
        if (Array.isArray(videoData) && videoData.length > 0) {
          videoData = videoData[0];
        }
        // Check both direct properties and props field
        let src = '';
        let name = 'Video';
        if (videoData) {
          src = videoData.src || (videoData.props && videoData.props.src) || '';
          name = videoData.name || (videoData.props && videoData.props.name) || 'Video';
        }
        if (src) {
          text = `[Video: ${name}](${src})`;
        }
      } else if (block.type === 'File' && block.value) {
        // Handle file blocks - value might be an array
        let fileData = block.value;
        if (Array.isArray(fileData) && fileData.length > 0) {
          fileData = fileData[0];
        }
        // Check both direct properties and props field
        let src = '';
        let name = 'File';
        if (fileData) {
          src = fileData.src || (fileData.props && fileData.props.src) || '';
          name = fileData.name || (fileData.props && fileData.props.name) || 'File';
        }
        if (src) {
          text = `[File: ${name}](${src})`;
        }
      } else if (block.type === 'Table' && block.value) {
        // Handle table blocks - convert to GitHub Flavored Markdown
        let tableData = block.value;
        if (Array.isArray(tableData) && tableData.length > 0) {
          tableData = tableData[0];
        }

        if (tableData && tableData.children && Array.isArray(tableData.children)) {
          const rows = tableData.children;
          let tableMarkdown = '';

          // Process each row
          rows.forEach((row: any, rowIndex: number) => {
            if (row.children && Array.isArray(row.children)) {
              const cells = row.children.map((cell: any) => {
                // Extract text from cell children, handling formatted text
                if (cell.children && Array.isArray(cell.children)) {
                  return cell.children.map((child: any) => {
                    let cellText = child.text || '';
                    // Preserve formatting
                    if (child.bold && cellText.trim()) cellText = `**${cellText}**`;
                    if (child.italic && cellText.trim()) cellText = `*${cellText}*`;
                    if (child.code && cellText.trim()) cellText = `\`${cellText}\``;
                    return cellText;
                  }).join('');
                }
                return '';
              });

              // Build table row in markdown format
              tableMarkdown += '| ' + cells.join(' | ') + ' |\n';

              // Add separator after first row (header row)
              if (rowIndex === 0) {
                const separators = cells.map(() => '---');
                tableMarkdown += '| ' + separators.join(' | ') + ' |\n';
              }
            }
          });

          text = tableMarkdown.trim();
        }
      } else if (block.value && Array.isArray(block.value)) {
        // Handle text blocks - extract text from children
        for (const item of block.value) {
          if (item && typeof item === 'object' && item.children && Array.isArray(item.children)) {
            for (const child of item.children) {
              if (child && typeof child.text === 'string') {
                // Handle formatted text - prevent double formatting
                let childText = child.text;
                
                // Clean existing markdown first
                childText = childText.replace(/\*\*(.*?)\*\*/g, '$1'); // Remove existing bold
                childText = childText.replace(/\*(.*?)\*/g, '$1'); // Remove existing italic
                childText = childText.replace(/`(.*?)`/g, '$1'); // Remove existing code
                
                // Apply formatting only if flag is set
                if (child.bold && childText.trim()) childText = `**${childText}**`;
                if (child.italic && childText.trim()) childText = `*${childText}*`;
                if (child.code && childText.trim()) childText = `\`${childText}\``;
                text += childText;
              }
            }
          }
        }
      } else if (block.value && typeof block.value === 'object' && !Array.isArray(block.value)) {
        // Handle single value objects (like images)
        if (block.value.src) {
          const alt = block.value.alt || block.value.name || 'Image';
          text = `![${alt}](${block.value.src})`;
        } else if (block.value.text) {
          text = block.value.text;
        }
      }

      // Skip empty blocks
      if (!text.trim()) continue;

      // Format based on block type
      switch (block.type) {
        case 'HeadingOne':
          result += `# ${text}\n\n`;
          break;
        case 'HeadingTwo':
          result += `## ${text}\n\n`;
          break;
        case 'HeadingThree':
          result += `### ${text}\n\n`;
          break;
        case 'Blockquote':
          result += `> ${text}\n\n`;
          break;
        case 'BulletedList':
          result += `- ${text}\n`;
          break;
        case 'NumberedList':
          result += `1. ${text}\n`;
          break;
        case 'Code':
          result += `\`\`\`\n${text}\n\`\`\`\n\n`;
          break;
        case 'Image':
        case 'Video':
        case 'File':
          // Media blocks - text already contains markdown
          result += `${text}\n\n`;
          break;
        case 'Table':
          // Table blocks - text already contains GFM table markdown
          result += `${text}\n\n`;
          break;
        case 'Paragraph':
        default:
          result += `${text}\n\n`;
          break;
      }
    }

    return result.trim() || '';
  } catch (error) {
    console.error('Failed to convert Yoopta to markdown:', error);
    return '';
  }
}

/**
 * Convert all sections from markdown to include Yoopta values
 */
export function convertSectionsToYoopta(sections: any[]): any[] {
  console.log('🔄 Converting all sections to Yoopta format:', sections.length);
  
  const convertedSections = sections.map((section, index) => {
    console.log(`📝 Converting section ${index + 1}:`, {
      id: section.id,
      title: section.title,
      contentLength: section.content?.length || 0
    });
    
    // Convert to Yoopta (this now always returns a valid value)
    const finalYooptaValue = convertMarkdownToYoopta(section.content);
    
    const convertedSection = {
      ...section,
      yooptaValue: finalYooptaValue, // Always a valid Yoopta value
      originalContent: section.content, // Keep original content as backup
    };
    
    console.log(`✅ Section ${index + 1} converted:`, {
      hasYooptaValue: !!convertedSection.yooptaValue,
      yooptaKeys: Object.keys(convertedSection.yooptaValue)
    });
    
    return convertedSection;
  });

  console.log('✅ All sections converted to Yoopta format');
  return convertedSections;
}

export { plugins as yooptaPlugins, conversionEditor };